import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, FileText, Building2, IndianRupee, UploadCloud, Sparkles, Loader2, Trash2, Bot, Check,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import PhoneInput from "@/components/PhoneInput";
import { isSubmittablePhone } from "@/lib/phone";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  NexaPageHeader, NexaSearch, NexaKPI, NexaEmpty, NexaChipTabs,
  NexaAIInsight, NexaModal, NexaField, NexaStatusChip,
} from "@/components/nexa";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// Small emerald chip shown next to a Paid invoice when the vendor was
// successfully auto-notified via WhatsApp. Quiet proof of Nexa working.
function VendorNotifiedChip({ at, invoiceId }) {
  const [d, timeLabel, dateLabel] = (() => {
    try {
      const dt = new Date(at);
      const date = dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
      const time = dt.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
      return [dt, time, date];
    } catch { return [null, "", ""]; }
  })();
  const tip = d ? `Vendor notified automatically\nWhatsApp • ${dateLabel}, ${timeLabel}` : "Vendor notified automatically";
  return (
    <span
      className="inline-flex items-center gap-1 h-4 px-1.5 rounded-full text-[8.5px] font-medium border border-emerald-200 bg-emerald-50 text-emerald-700"
      title={tip}
      aria-label={tip}
      data-testid={`inv-notified-${invoiceId}`}
    >
      <Check className="w-2 h-2" strokeWidth={2.5} />
      Vendor notified
    </span>
  );
}

export default function Vendors() {
  const nav = useNavigate();
  const [tab, setTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [insight, setInsight] = useState(null);
  const [creatingInv, setCreatingInv] = useState(false);
  const [creatingVen, setCreatingVen] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [automation, setAutomation] = useState({ payment_reminders_enabled: false, payment_reminder_stages: ["3d","1d","due"] });

  const load = useCallback(async () => {
    try {
      const [{ data: i }, { data: v }, { data: s }, { data: ins }, { data: aut }] = await Promise.all([
        api.get("/invoices"),
        api.get("/vendors"),
        api.get("/vendors/summary"),
        api.get("/vendors/insight"),
        api.get("/settings/automation").catch(() => ({ data: null })),
      ]);
      setInvoices(i.invoices || []);
      setVendors(v.vendors || []);
      setSummary(s);
      setInsight(ins);
      if (aut) setAutomation(aut);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markPaid = async (id) => {
    try {
      const { data } = await api.patch(`/invoices/${id}`, { payment_status: "paid" });
      const n = data?.notification;
      if (n?.success) {
        toast.success("Invoice marked as paid.", { description: "Vendor notification sent successfully." });
      } else if (n && !n.success) {
        toast.success("Invoice marked as paid.", { description: "Vendor notification could not be delivered." });
      } else {
        toast.success("Invoice marked as paid.");
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeInvoice = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeVendor = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    try {
      await api.delete(`/vendors/${id}`);
      toast.success("Vendor deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filteredInv = invoices.filter((r) => {
    const s = q.trim().toLowerCase();
    const matches = !s || [r.vendor_name, r.invoice_number, r.status].some((v) => (v || "").toLowerCase().includes(s));
    const okStatus = statusFilter === "all" || r.status === statusFilter;
    return matches && okStatus;
  });

  const filteredVen = vendors.filter((v) => {
    const s = q.trim().toLowerCase();
    return !s || [v.company_name, v.gst, v.email].some((x) => (x || "").toLowerCase().includes(s));
  });

  return (
    <div className="min-h-screen bg-nexa-paper">
      <Sidebar />
      <main className="nexa-compact lg:ml-[260px] min-h-screen">
        <div className="w-full px-3 md:px-5 py-3 md:py-4">
        <NexaPageHeader
          title="Vendor & Invoice Management"
          subtitle="Your AI Executive manages vendors, invoices, payments and cash flow."
          testId="ven-page-header"
          actions={
            <>
              <NexaSearch
                className="w-64"
                value={q}
                onChange={setQ}
                placeholder="Search vendor, invoice, GST"
                testId="ven-search"
              />
              <button onClick={() => setCreatingVen(true)} className="lux-btn-secondary" data-testid="ven-add-vendor">
                <Building2 className="w-4 h-4 mr-1.5" strokeWidth={1.7} /> Vendor
              </button>
              <button onClick={() => setCreatingInv(true)} className="lux-btn-accent" data-testid="ven-add-invoice">
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={2} /> New invoice
              </button>
            </>
          }
        />

        {/* AI Vendor Executive hero */}
        {insight && (
          <div className="mb-3">
            <NexaAIInsight
              eyebrow="AI Vendor & Invoice Executive"
              status="Working normally · Today's activity"
              completed={insight.completed}
              attention={insight.attention}
              metricLabel="Cash flow health"
              metricValue={`${insight.cash_health}%`}
              testId="ven-hero"
            />
          </div>
        )}

        {/* KPI strip */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3" data-testid="ven-kpis">
            <NexaKPI label="Outstanding" value={INR(summary.outstanding)} index={0} />
            <NexaKPI label="Paid" value={summary.paid} index={1} />
            <NexaKPI label="Due today"   value={INR(summary.due_today)}   index={2} />
            <NexaKPI label="Overdue"     value={INR(summary.overdue)}     index={3} />
     
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <NexaChipTabs
            testIdPrefix="ven-tab"
            value={tab}
            onChange={setTab}
            items={[
              { value: "invoices", label: "Invoices", count: invoices.length },
              { value: "vendors",  label: "Vendors",  count: vendors.length },
            ]}
          />
          {tab === "invoices" && (
            <NexaChipTabs
              size="sm"
              testIdPrefix="inv-filter"
              value={statusFilter}
              onChange={setStatusFilter}
              items={[
                { value: "all",     label: "All" },
                { value: "pending", label: "Pending" },
                { value: "paid",    label: "Paid" },
                { value: "overdue", label: "Overdue" },
              ]}
            />
          )}
        </div>

        {tab === "invoices" && (
          <div className="lux-card overflow-hidden">
            {filteredInv.length === 0 ? (
              <NexaEmpty
                icon={FileText}
                title="Your AI Executive is ready to manage your vendor operations."
                subtitle="Create your first invoice — or upload a scan and let AI extract the details."
                testId="inv-empty"
                action={
                  <button className="lux-btn-accent" onClick={() => setCreatingInv(true)} data-testid="inv-empty-cta">
                    <Plus className="w-3 h-3 mr-1" strokeWidth={2} /> New invoice
                  </button>
                }
              />
            ) : (
              <table className="w-full text-[10.5px]">
                <thead className="text-[8px] tracking-[0.14em] uppercase text-muted: #7A8794; font-semibold border-b border-nexa-line">
                  <tr>{["Invoice", "Vendor", "Due", "Amount", "Status", ""].map((h, i) =>
                    <th key={i} className="text-left px-3 py-2">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {filteredInv.map((r) => (
                    <tr key={r.invoice_id} className="border-b border-nexa-line last:border-0 hover:bg-nexa-canvas/40 transition-colors" data-testid={`inv-row-${r.invoice_id}`}>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-nexa-ink">{r.invoice_number}</div>
                        {r.notes && (
                          <div className="text-[9px] text-nexa-mute-2 mt-0.5 truncate max-w-[28ch]" title={r.notes} data-testid={`inv-notes-${r.invoice_id}`}>
                            {r.notes}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-nexa-ink-2">{r.vendor_name}</td>
                      <td className="px-3 py-2.5 text-nexa-mute">{r.due_date}</td>
                      <td className="px-3 py-2.5 font-medium text-nexa-ink">{INR(r.total)}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <NexaStatusChip status={r.status} />
                          {r.payment_status === "paid" && r.vendor_notified_at && (
                            <VendorNotifiedChip at={r.vendor_notified_at} invoiceId={r.invoice_id} />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {r.payment_status !== "paid" && (
                            <button
                                onClick={() => markPaid(r.invoice_id)}
                                data-testid={`inv-pay-${r.invoice_id}`}
                                className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 text-[9px] font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                              >
                                <IndianRupee className="w-2 h-2" strokeWidth={1.8} />
                                Mark Paid
                            </button>
                          )}
                          <button onClick={() => removeInvoice(r.invoice_id)} className="lux-btn-quiet text-nexa-mute hover:text-nexa-danger" data-testid={`inv-delete-${r.invoice_id}`} aria-label="Delete">
                            <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "vendors" && (
          <div className="lux-card overflow-hidden">
            {filteredVen.length === 0 ? (
              <NexaEmpty
                icon={Building2}
                title="No vendors yet."
                subtitle="Create one, or upload an invoice — the vendor is created automatically."
                testId="ven-empty"
                action={
                  <button className="lux-btn-accent" onClick={() => setCreatingVen(true)}>
                    <Plus className="w-3 h-3 mr-1" strokeWidth={2} /> New vendor
                  </button>
                }
              />
            ) : (
              <table className="w-full text-[10.5px]">
                <thead className="text-[8px] tracking-[0.14em] uppercase text-nexa-mute font-semibold border-b border-nexa-line">
                  <tr>{["Vendor", "Service Category", "GST", "Terms", "Outstanding", "Invoices", ""].map((h, i) =>
                    <th key={i} className="text-left px-3 py-2">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {filteredVen.map((v) => (
                    <tr key={v.vendor_id} className="border-b border-nexa-line last:border-0 hover:bg-nexa-canvas/40" data-testid={`ven-row-${v.vendor_id}`}>
                      <td className="px-3 py-2.5 font-medium text-nexa-ink">{v.company_name}</td>
                      <td className="px-3 py-2.5 text-nexa-mute" data-testid={`ven-cat-${v.vendor_id}`}>{v.service_category || "—"}</td>
                      <td className="px-3 py-2.5 text-nexa-mute">{v.gst || "—"}</td>
                      <td className="px-3 py-2.5 text-nexa-mute">{v.payment_terms || "Net 30"}</td>
                      <td className="px-3 py-2.5 font-medium text-nexa-ink">{INR(v.outstanding)}</td>
                      <td className="px-3 py-2.5 text-nexa-mute">{v.invoices_count || 0}</td>
                      <td className="px-3 py-2.5 text-right">
                        <button onClick={() => removeVendor(v.vendor_id)} className="lux-btn-quiet text-nexa-mute hover:text-nexa-danger" data-testid={`ven-delete-${v.vendor_id}`} aria-label="Delete">
                          <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        </div>
      </main>

      <InvoiceModal open={creatingInv} onClose={() => setCreatingInv(false)} onDone={load} vendors={vendors} />
      <VendorModal  open={creatingVen} onClose={() => setCreatingVen(false)} onDone={load} />
    </div>
  );
}

/* -------------------- Invoice Modal with AI Extraction -------------------- */

function InvoiceModal({ open, onClose, onDone, vendors }) {
  const today = new Date().toISOString().slice(0, 10);
  const blank = {
      company_name: "",
      gst: "",
      email: "",
      phone: "",
      service_category: "",
      payment_terms: "Net 30",
      priority: "normal",
      is_active: true,
  };
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [aiPill, setAiPill] = useState(null); // null | { count }
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => { if (open) { setF(blank); setAiPill(null); } }, [open]);

  const runExtraction = async (file) => {
    if (!file) return;
    const ok = ["application/pdf", "image/png", "image/jpeg", "image/webp"].includes(file.type) ||
               /\.(pdf|png|jpe?g|webp)$/i.test(file.name || "");
    if (!ok) { toast.error("Upload a PDF, PNG, JPG or WEBP"); return; }
    setExtracting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/invoices/extract", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const e = data.extracted || {};
      const merged = {
        vendor_name:   e.vendor_name || "",
        invoice_number: e.invoice_number || "",
        invoice_date:  e.invoice_date || today,
        due_date:      e.due_date || today,
        subtotal:      e.subtotal ?? 0,
        gst:           e.gst ?? 0,
        total:         e.total ?? 0,
        invoice_description: e.invoice_description || "",
      };
      setF(merged);
      const filled = Object.values(merged).filter((v) => v !== "" && v !== 0 && v !== null).length;
      setAiPill({ count: filled });
      toast.success(`AI extracted ${filled} fields — review and save`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    runExtraction(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/invoices", {
        ...f,
        subtotal: Number(f.subtotal), gst: Number(f.gst), total: Number(f.total),
      });
      toast.success("Invoice created — vendor linked");

      setF(blank);
      setAiPill(null);

      await onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <NexaModal
      open={open}
      onClose={onClose}
      size="xl"
      title="New invoice"
      subtitle="Drag an invoice scan for AI extraction, or fill it manually."
      testId="inv-modal"
    >
      {/* AI Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative rounded-xl border-2 border-dashed transition-all p-5 mb-5 ${
          dragOver ? "border-nexa-accent bg-nexa-accent/5" : "border-nexa-line bg-nexa-canvas/50"
        }`}
        data-testid="inv-dropzone"
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-12 rounded-full bg-nexa-accent/10 flex items-center justify-center shrink-0">
            {extracting
              ? <Loader2 className="w-5 h-5 text-nexa-accent animate-spin" strokeWidth={2} />
              : <UploadCloud className="w-5 h-5 text-nexa-accent" strokeWidth={1.7} />}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="text-[14px] font-semibold text-nexa-ink">
                {extracting ? "AI is reading your invoice…" : "Drop invoice here"}
              </div>
              {aiPill && (
                <span className="lux-chip lux-chip-accent" data-testid="inv-ai-pill">
                  <Sparkles className="w-3 h-3" strokeWidth={2} /> AI filled {aiPill.count} fields
                </span>
              )}
            </div>
            <div className="text-[12.5px] text-nexa-mute mt-0.5">
              PDF, PNG, JPG or WEBP · up to 8 MB · powered by Gemini
            </div>
          </div>
          <label className={`lux-btn-secondary cursor-pointer ${extracting ? "opacity-50 pointer-events-none" : ""}`} data-testid="inv-file-picker">
            Browse
            <input
              type="file"
              className="hidden"
              accept=".pdf,image/png,image/jpeg,image/webp"
              onChange={(e) => runExtraction(e.target.files?.[0])}
              data-testid="inv-file-input"
            />
          </label>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="inv-form">
        <NexaField label="Vendor">
          <input
            required list="ven-list"
            className="lux-input"
            value={f.vendor_name}
            onChange={(e) => set("vendor_name", e.target.value)}
            data-testid="inv-vendor"
            placeholder="Select Vendors"
          />
          <datalist id="ven-list">
            {vendors.map((v) => <option key={v.vendor_id} value={v.company_name} />)}
          </datalist>
        </NexaField>
        <div className="grid grid-cols-2 gap-3">
          <NexaField label="Invoice #">
            <input required className="lux-input" value={f.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} data-testid="inv-number" />
          </NexaField>
          <NexaField label="Total (₹)">
            <input required type="number" min="0" step="0.01" className="lux-input" value={f.total} onChange={(e) => set("total", e.target.value)} data-testid="inv-total" />
          </NexaField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NexaField label="Invoice date">
            <input required type="date" className="lux-input" value={f.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} data-testid="inv-invoice-date" />
          </NexaField>
          <NexaField label="Due date">
            <input required type="date" className="lux-input" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="inv-due-date" />
          </NexaField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NexaField label="Subtotal (₹)">
            <input type="number" min="0" step="0.01" className="lux-input" value={f.subtotal} onChange={(e) => set("subtotal", e.target.value)} data-testid="inv-subtotal" />
          </NexaField>
          <NexaField label="GST (₹)">
            <input type="number" min="0" step="0.01" className="lux-input" value={f.gst} onChange={(e) => set("gst", e.target.value)} data-testid="inv-gst" />
          </NexaField>
        </div>
        <NexaField
          label="Invoice Description"
          hint="Brief description of the purchased item or service"
        >
          <input
            className="lux-input"
            value={f.invoice_description}
            onChange={(e) => set("invoice_description", e.target.value)}
            data-testid="inv-description"
            placeholder="e.g. Dental Chair, Office Supplies, Website Maintenance"
          />
        </NexaField>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="lux-btn-secondary">Cancel</button>
          <button disabled={saving || extracting} className="lux-btn-accent" data-testid="inv-submit">
            {saving ? "Saving…" : "Create invoice"}
          </button>
        </div>
      </form>
    </NexaModal>
  );
}

/* -------------------- Vendor Modal -------------------- */

function VendorModal({ open, onClose, onDone }) {
  const blank = { company_name: "", gst: "", email: "", phone: "", service_category: "", payment_terms: "Net 30", priority: "normal", is_active: true };
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(blank); }, [open]);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/vendors", f);
      toast.success("Vendor created");
      onDone(); onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <NexaModal open={open} onClose={onClose} title="New vendor" testId="ven-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="ven-form">
        <NexaField label="Company">
          <input required className="lux-input" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} data-testid="ven-company" />
        </NexaField>
        <div className="grid grid-cols-2 gap-3">
          <NexaField label="GSTIN">
            <input className="lux-input" value={f.gst} onChange={(e) => setF({ ...f, gst: e.target.value })} data-testid="ven-gst" />
          </NexaField>
          <NexaField label="Phone">
            <PhoneInput
              value={f.phone}
              onChange={(v) => setF({ ...f, phone: v })}
              testId="ven-phone"
              required={false}
            />
          </NexaField>
        </div>
        <NexaField label="Email">
          <input type="email" className="lux-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="ven-email" />
        </NexaField>
        <NexaField label="Service Category">
          <select
            required
            className="lux-input"
            value={f.service_category || ""}
            onChange={(e) =>
              setF({ ...f, service_category: e.target.value })
            }
            data-testid="ven-service-category"
          >
            <option value="">Select Service Category</option>
            <option value="Medical Supplies">Medical Supplies</option>
            <option value="Dental Supplies">Dental Supplies</option>
            <option value="Beauty Products">Beauty Products</option>
            <option value="Cleaning Supplies">Cleaning Supplies</option>
            <option value="Food & Beverage">Food & Beverage</option>
            <option value="Office Supplies">Office Supplies</option>
            <option value="Equipment">Equipment</option>
            <option value="IT Services">IT Services</option>
            <option value="Marketing">Marketing</option>
            <option value="Maintenance">Maintenance</option>
            <option value="Utilities">Utilities</option>
            <option value="Other">Other</option>
          </select>
        </NexaField>
        <NexaField label="Payment terms">
          <input className="lux-input" value={f.payment_terms} onChange={(e) => setF({ ...f, payment_terms: e.target.value })} data-testid="ven-terms" />
        </NexaField>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="lux-btn-secondary">Cancel</button>
          <button disabled={saving || !isSubmittablePhone(f.phone, { required: false })} className="lux-btn-accent" data-testid="ven-submit">
            {saving ? "Saving…" : "Create vendor"}
          </button>
        </div>
      </form>
    </NexaModal>
  );
}


